<template>
    <div class="flex flex-row gap-4 flex-wrap items-stretch">
        <HomeActionItemCard
            v-if="visibleCardTypes.includes(CARD_TYPE.ACTION_ITEMS)"
            class="w-full sm:min-w-60 sm:max-w-72 sm:flex-1"
            :action-items="actionItems"
            :line-clamp="2"
            :default_chat_redirect_url="defaultChatRedirectUrl" />
        <HomeWrappedActionItemsCard
            v-if="visibleCardTypes.includes(CARD_TYPE.WRAPPED_ACTION_ITEMS)"
            class="w-full sm:min-w-60 sm:max-w-72 sm:flex-1"
            :card-type="CARD_TYPE.WRAPPED_ACTION_ITEMS"
            :action-items="wrapped.action_items ?? []" />
        <HomeUserFeedbackCard v-if="visibleCardTypes.includes(CARD_TYPE.USER_FEEDBACK)" class="w-full sm:w-1/2" :user-feedback="userFeedback" />
        <NewHomeCard
            v-if="visibleCardTypes.includes(CARD_TYPE.FOLLOW_UP)"
            class="w-full sm:min-w-60 sm:max-w-72 sm:flex-1"
            :card-type="FOLLOW_UP_CARD.type"
            :title="t('chat.home.followUp.title')"
            :analytics-title="FOLLOW_UP_CARD.title"
            :content="t('chat.home.followUp.content')"
            :cta-button-text="t('chat.home.followUp.cta')"
            @cta-click="$emit('cta-click', FOLLOW_UP_CARD)" />
        <HomeWrappedCard
            v-if="visibleCardTypes.includes(CARD_TYPE.WRAPPED)"
            class="w-full sm:min-w-60 sm:max-w-72 sm:flex-1"
            :card-type="CARD_TYPE.WRAPPED"
            :wrapped-url="wrapped.url"
            :wrapped-year="Number(wrapped.year) || 0"
            :action-items="wrapped.action_items ?? []" />
    </div>
</template>

<script setup lang="ts">
import { convertToDateTime } from "/js/dateUtils";
import HomeActionItemCard from "~vue/ChatHome/HomeActionItemCard.vue";
import HomeUserFeedbackCard from "~vue/ChatHome/HomeUserFeedbackCard.vue";
import HomeWrappedActionItemsCard from "~vue/ChatHome/HomeWrappedActionItemsCard.vue";
import HomeWrappedCard from "~vue/ChatHome/HomeWrappedCard.vue";
import NewHomeCard from "~vue/ChatHome/NewHomeCard.vue";
import type { ActionItem, CoachFollowupEvent, UserFeedback, Wrapped } from "~vue/types/chat_home";
import { CARD_TYPE } from "~vue/utils/homeCards";
import { DateTime } from "luxon";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    actionItems?: ActionItem[];
    coachFollowUpEvents?: CoachFollowupEvent[];
    defaultChatRedirectUrl?: string;
    userFeedback?: UserFeedback;
    wrapped?: Wrapped;
}

const props = withDefaults(defineProps<Props>(), {
    actionItems: () => [],
    coachFollowUpEvents: () => [],
    defaultChatRedirectUrl: "/",
    userFeedback: () => ({ enabled: false, feedback_response_count_min: null }),
    wrapped: () => ({ can_access: false, can_start: false, show_completed: false, url: "", year: 0, action_items: [] }),
});

defineEmits(["cta-click"]);

const { t } = useI18n();

const followUpEventToday = computed<CoachFollowupEvent | null>(
    () => props.coachFollowUpEvents.find((event) => DateTime.now().hasSame(convertToDateTime(event.event_at_confirmed), "day")) ?? null,
);

const FOLLOW_UP_CARD = computed(() => ({
    type: CARD_TYPE.FOLLOW_UP,
    // Only read by analytics (HOMEPAGE_CARD_SHOWN/CLICKED), which must stay in English; the card displays chat.home.followUp.title.
    title: "Time for your follow-up",
    url: followUpEventToday.value?.custom_link ?? props.defaultChatRedirectUrl,
}));

const visibleCardTypes = computed(() => {
    const result = [];
    let maxCardCount = 3;

    if (props.actionItems.length) {
        result.push(CARD_TYPE.ACTION_ITEMS);
    }
    if (!props.wrapped.show_completed && props.wrapped.action_items.length) {
        // Show "Keep the momentum going" card when Wrapped season is over but user has action items
        result.push(CARD_TYPE.WRAPPED_ACTION_ITEMS);
    }
    if (props.userFeedback?.enabled) {
        result.push(CARD_TYPE.USER_FEEDBACK);
        // The Feedback card is wider than the others, so it limits us to only 2 cards
        maxCardCount = 2;
    }
    if (followUpEventToday.value?.event_at_confirmed) {
        result.push(CARD_TYPE.FOLLOW_UP);
    }
    if (props.wrapped.show_completed) {
        result.push(CARD_TYPE.WRAPPED);
    }

    return result.slice(0, maxCardCount);
});
</script>
